/**
 * CORE-F — display vocabulary
 *
 * Pure presentation mapping. No React, no I/O, no clock — so the wording
 * shown to a technician about their own record is testable as data rather
 * than only reachable by rendering a component.
 *
 * Accessibility is a constitutional requirement here, not a polish item.
 * The constitution requires that capability "must not be communicated by
 * colour alone", so every state carries THREE independent channels:
 *
 *   1. colour   — `tone`
 *   2. glyph    — `glyph`, a text marker that survives greyscale and
 *                 colour-blindness
 *   3. words    — `label`, plus `describeCell` for assistive technology
 *
 * Any one channel alone is enough to distinguish "never worked" from
 * "worked and weak". That is the point: those two readings must never be
 * confusable, and a person using a screen reader or a monochrome display
 * must be able to tell them apart as easily as anyone else.
 */

import {
  HEATMAP_DIMENSION_CLAIM,
  HEATMAP_DIMENSION_EVIDENCE_SOURCE,
  HEATMAP_DIMENSION_OWNER,
  type DimensionState,
  type EvidenceStrength,
  type HeatmapCell,
  type HeatmapDimensionKey,
  type ProficiencyBand,
  type ProficiencyDivergence,
  type RecencyBand,
} from './types';

// =============================================================================
// Proficiency bands
// =============================================================================

export interface BandPresentation {
  label: string;
  /** Non-colour channel. */
  glyph: string;
  /** Tailwind classes. */
  tone: string;
  /** Plain-language meaning, shown in detail and read by assistive tech. */
  meaning: string;
}

/**
 * How each band looks and reads.
 *
 * `unknown` is deliberately NOT on the red-to-green ramp. It is neutral,
 * dashed and outlined — visually a gap in the record rather than a low
 * point on a scale, because that is exactly what it is. The old build gave
 * it `bg-destructive/30` and the word "Novice"; a technician looking at a
 * category they had never been assigned saw the same thing they would have
 * seen after failing at it repeatedly.
 */
export const BAND_PRESENTATION: Readonly<
  Record<ProficiencyBand, BandPresentation>
> = Object.freeze({
  unknown: {
    label: 'No data',
    glyph: '—',
    tone: 'bg-muted/40 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning: 'No completed work recorded in this category yet.',
  },
  novice: {
    label: 'Novice',
    glyph: '▁',
    tone: 'bg-destructive/30 text-foreground',
    meaning: 'Early-stage demonstrated capability.',
  },
  developing: {
    label: 'Developing',
    glyph: '▃',
    tone: 'bg-warning/30 text-foreground',
    meaning: 'Building demonstrated capability.',
  },
  capable: {
    label: 'Capable',
    glyph: '▅',
    tone: 'bg-warning/60 text-foreground',
    meaning: 'Solid demonstrated capability.',
  },
  proficient: {
    label: 'Proficient',
    glyph: '▆',
    tone: 'bg-success/50 text-foreground',
    meaning: 'Strong demonstrated capability.',
  },
  expert: {
    label: 'Expert',
    glyph: '█',
    tone: 'bg-success/80 text-success-foreground',
    meaning: 'Consistently high demonstrated capability.',
  },
});

// =============================================================================
// Non-value states
// =============================================================================

/**
 * How the three non-value states present.
 *
 * Each gets its own wording and its own glyph. A reader must be able to
 * tell "still loading", "we could not read this" and "there is nothing
 * here" apart at a glance — collapsing them is how a transient outage
 * starts looking like a permanent fact about a person.
 */
export const STATE_PRESENTATION = Object.freeze({
  loading: {
    label: 'Loading',
    glyph: '·',
    tone: 'bg-muted/30 text-muted-foreground animate-pulse',
    meaning: 'Still loading.',
  },
  read_failed: {
    label: 'Unavailable',
    glyph: '!',
    tone: 'bg-muted/40 text-muted-foreground border border-dashed border-destructive/50',
    meaning: 'Could not be loaded. This is a load failure, not a result.',
  },
  not_permitted: {
    label: 'Not visible to you',
    glyph: '⊘',
    tone: 'bg-muted/30 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning:
      'Your access does not include this source. This says nothing about the person\'s record.',
  },
  not_linked: {
    label: 'Not linked',
    glyph: '?',
    tone: 'bg-muted/30 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning:
      'This Passport is not linked to a shop record that carries this data.',
  },
  unsupported: {
    label: 'Not available here',
    glyph: '?',
    tone: 'bg-muted/30 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning: 'This dimension is not available on this view.',
  },
  insufficient_basis: {
    label: 'Basis incomplete',
    glyph: '~',
    tone: 'bg-muted/30 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning:
      'Completed work exists here, but it does not carry what is needed to work this figure out without guessing at part of it.',
  },
  no_evidence: {
    label: 'No data',
    glyph: '—',
    tone: 'bg-muted/40 text-muted-foreground border border-dashed border-muted-foreground/40',
    meaning: 'No evidence recorded yet.',
  },
});

export type StatePresentationKey = keyof typeof STATE_PRESENTATION;

/** The presentation key for a non-`known` state. */
export function stateKeyFor(state: DimensionState): StatePresentationKey | null {
  switch (state.status) {
    case 'loading':
      return 'loading';
    case 'unavailable':
      return state.reason;
    case 'no_evidence':
      return 'no_evidence';
    case 'known':
      return null;
  }
}

// =============================================================================
// Evidence strength
// =============================================================================

export const EVIDENCE_PRESENTATION: Readonly<
  Record<EvidenceStrength, { label: string; glyph: string; meaning: string }>
> = Object.freeze({
  unknown: {
    label: 'Sample unknown',
    glyph: '?',
    meaning: 'The amount of work behind this could not be established.',
  },
  none: {
    label: 'No completed work',
    glyph: '○',
    meaning: 'No completed jobs recorded behind this.',
  },
  sparse: {
    label: 'Limited evidence',
    glyph: '◔',
    meaning: 'Based on very few jobs — treat as provisional.',
  },
  adequate: {
    label: 'Moderate evidence',
    glyph: '◑',
    meaning: 'Based on a reasonable number of jobs.',
  },
  strong: {
    label: 'Well evidenced',
    glyph: '●',
    meaning: 'Based on a substantial amount of completed work.',
  },
});

// =============================================================================
// Recency
// =============================================================================

export const RECENCY_PRESENTATION: Readonly<
  Record<RecencyBand, { label: string; glyph: string; meaning: string }>
> = Object.freeze({
  unknown: {
    label: 'Date unknown',
    glyph: '?',
    meaning: 'No date recorded for the most recent evidence.',
  },
  recent: {
    label: 'Recent',
    glyph: '▲',
    meaning: 'Updated within the last 30 days.',
  },
  aging: {
    label: 'Aging',
    glyph: '◆',
    meaning: 'Last updated 1–3 months ago.',
  },
  older: {
    label: 'Older',
    glyph: '▼',
    // Stated explicitly, because a reader seeing "older" may otherwise
    // assume the system has already discounted the value. It has not.
    meaning:
      'Last updated more than 3 months ago. The value is not reduced for age.',
  },
});

// =============================================================================
// Values
// =============================================================================

/**
 * Render a dimension's value, or the word for why there isn't one.
 *
 * The only place a number becomes a string. It cannot invent one: a
 * non-`known` state has no `value` field to read.
 */
export function formatDimensionValue(
  key: HeatmapDimensionKey,
  state: DimensionState,
): string {
  if (state.status !== 'known') {
    const k = stateKeyFor(state);
    return k ? STATE_PRESENTATION[k].label : 'No data';
  }
  switch (key) {
    case 'proficiency':
    case 'diagnostic':
    case 'readiness':
      return `${Math.round(state.value)}%`;
    case 'efficiency':
      return `${Math.round(state.value)}% of expected`;
    case 'volume':
      return state.value === 1 ? '1 job' : `${Math.round(state.value)} jobs`;
  }
}

/**
 * The recorded sample size, as a count.
 *
 * The actual number, not just the band. CORE-E requires confidence and
 * sample size to travel with a proficiency, and "Moderate evidence" does not
 * tell a technician whether four jobs or nine sit behind their rating. The
 * coarse band stays alongside as an interpretation of the count, never as a
 * replacement for it.
 */
export function formatSampleCount(state: DimensionState): string {
  if (state.status !== 'known') {
    // No sample recorded is a real and common case: the DMC-F sync trigger
    // writes proficiency rows with no sample size at all.
    if (state.status === 'no_evidence') return 'Sample unknown';
    const k = stateKeyFor(state);
    return k ? STATE_PRESENTATION[k].label : 'Sample unknown';
  }
  const n = Math.round(state.value);
  return n === 1 ? '1 completed job' : n + ' completed jobs';
}

/**
 * The confidence exactly as recorded on the row.
 *
 * Called "recorded", never "derived" or "calculated". The sync trigger
 * writes a constant 0.5 and overwrites whatever was there on conflict, so a
 * stored confidence may be that constant rather than anything computed —
 * and nothing at rest distinguishes the two. Describing it as derived would
 * dress a placeholder up as a measurement.
 */
export function formatRecordedConfidence(state: DimensionState): string {
  if (state.status !== 'known') {
    if (state.status === 'no_evidence') return 'Confidence unavailable';
    const k = stateKeyFor(state);
    return k ? STATE_PRESENTATION[k].label : 'Confidence unavailable';
  }
  // Stored 0..1; shown as a percentage.
  return Math.round(state.value * 100) + '%';
}

/**
 * The shop's scheduling value, phrased as context rather than capability.
 *
 * Never banded, never given a capability word. It is a number the shop
 * schedules from, and on this screen that is all it is allowed to be.
 */
export function formatSchedulingContext(state: DimensionState): string | null {
  if (state.status === 'known') {
    return 'Shop scheduling value: ' + Math.round(state.value);
  }
  if (state.status === 'no_evidence') return 'No shop scheduling value set';
  return null;
}

export const DIMENSION_LABEL: Readonly<Record<HeatmapDimensionKey, string>> =
  Object.freeze({
    proficiency: 'Demonstrated proficiency',
    diagnostic: 'Diagnostic competency',
    readiness: 'Training readiness',
    efficiency: 'Efficiency',
    volume: 'Completed work',
  });

/**
 * A dimension's full attribution line.
 *
 * Carries owner and claim together so a reader can see not just the number
 * but what kind of thing it is and who computed it — the provenance
 * separation the constitution requires, made visible rather than implied.
 */
export function describeDimensionProvenance(
  key: HeatmapDimensionKey,
): string {
  const owner = HEATMAP_DIMENSION_OWNER[key];
  const mechanism = HEATMAP_DIMENSION_EVIDENCE_SOURCE[key];
  // Owner and update mechanism are named separately. Efficiency is the case
  // that matters: CORE-G owns the value and CORE-AN updates it, and CORE-AN
  // is explicit that it "does not own efficiency" and "must never become"
  // a second authority over it. Collapsing the two here would make this
  // screen the place that quietly created one.
  const attribution = mechanism
    ? `value: ${owner} · updated by: ${mechanism}`
    : `value: ${owner}`;
  return `${HEATMAP_DIMENSION_CLAIM[key]} · ${attribution}`;
}

/**
 * How the two CORE-E stores relate, in words.
 *
 * A disagreement is disclosed rather than resolved. CORE-E-GAP-001 means
 * positive evidence never reaches the scheduling store, so a technician who
 * has improved is routinely still scheduled on the older number — and being
 * told that is very different from being shown one figure and left to
 * assume it is the only one.
 */
export const DIVERGENCE_PRESENTATION: Readonly<
  Record<ProficiencyDivergence, { label: string; meaning: string } | null>
> = Object.freeze({
  none: null,
  agree: {
    label: 'Portable record and shop scheduling value agree',
    meaning:
      'Both of CORE-E’s stores hold the same figure for this category.',
  },
  differ: {
    label: 'Portable record and shop scheduling value differ',
    meaning:
      'The portable record and the value this shop schedules from disagree. Demonstrated improvement does not currently flow into the scheduling value, so the scheduling figure can lag behind the work.',
  },
  portable_only: {
    label: 'No scheduling value set for this category',
    meaning:
      'The current shop has not set a scheduling value here. That is not a zero, and it does not affect the portable record.',
  },
  scheduling_only: {
    label: 'Shop scheduling value only',
    meaning:
      'No portable record exists for this category, so the figure shown is the value the current shop schedules from.',
  },
});

// =============================================================================
// Assistive-technology text
// =============================================================================

/**
 * The full spoken description of a cell.
 *
 * This is what a screen-reader user gets, and it must be at least as
 * informative as the visual cell — including the distinction the colours
 * carry. So it always states the band in words, and for an unknown
 * category it says so outright rather than reading out a number that isn't
 * there.
 */
export function describeCell(cell: HeatmapCell): string {
  const parts: string[] = [cell.category];

  if (cell.proficiency.status === 'known') {
    // Name the store every time. The portable record and the shop's
    // scheduling value mean different things, and a spoken description that
    // says only "proficiency" hides the difference from precisely the
    // people who cannot see the label.
    parts.push(
      `Passport proficiency record ${Math.round(cell.proficiency.value)} percent, ${BAND_PRESENTATION[cell.band].label}`,
    );
    parts.push(EVIDENCE_PRESENTATION[cell.evidence].label.toLowerCase());
    parts.push(RECENCY_PRESENTATION[cell.recency].label.toLowerCase());
    const divergence = DIVERGENCE_PRESENTATION[cell.divergence];
    if (cell.divergence === 'differ' && divergence) {
      parts.push(divergence.label.toLowerCase());
    }
  } else {
    const k = stateKeyFor(cell.proficiency);
    const p = k ? STATE_PRESENTATION[k] : STATE_PRESENTATION.no_evidence;
    // Say what it is, not a number. "No proficiency data recorded" is a
    // different sentence from "0 percent", and the difference matters.
    parts.push(`${p.label.toLowerCase()} — ${p.meaning}`);
  }

  const extras: string[] = [];
  if (cell.diagnostic.status === 'known') {
    extras.push(
      `diagnostic competency ${Math.round(cell.diagnostic.value)} percent`,
    );
  }
  if (cell.readiness.status === 'known') {
    extras.push(`training readiness ${Math.round(cell.readiness.value)} percent`);
  }
  if (cell.efficiency.status === 'known') {
    extras.push(
      `efficiency ${Math.round(cell.efficiency.value)} percent of expected${
        cell.efficiencyPartial ? ', partial' : ''
      }`,
    );
  }
  if (cell.volume.status === 'known') {
    extras.push(formatDimensionValue('volume', cell.volume));
  }
  if (extras.length > 0) parts.push(extras.join(', '));

  // The shop's scheduling number, spoken as plainly as it is drawn. A
  // screen-reader user must get the same two-coordinate picture a sighted
  // one does — including, when the Passport record is empty, that a shop
  // number exists at all and is not being treated as their capability.
  const schedulingSpoken = formatSchedulingContext(cell.scheduling);
  if (cell.scheduling.status === 'known' && schedulingSpoken) {
    parts.push(schedulingSpoken);
  }

  // Some parts already end in a full stop; joining blindly would speak a
  // stray extra pause.
  return parts.map((part) => part.replace(/\.\s*$/, '')).join('. ') + '.';
}

/**
 * The banner shown when whole sources failed.
 *
 * Named explicitly so a reader is told the map is incomplete instead of
 * quietly seeing a sparser picture and believing it. A person should never
 * have to guess whether a gap on this screen is their record or our outage.
 */
export function describeSourceFailures(
  failed: ReadonlyArray<HeatmapDimensionKey>,
): string | null {
  if (failed.length === 0) return null;
  const names = failed.map((k) => DIMENSION_LABEL[k]);
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} could not be loaded. Those dimensions are shown as unavailable, not as zero.`;
}
